import { useState } from 'react';
import { AppBundle, getImportShim } from '../utils/app-bundles';
import { APP_EXTERNAL_UI_URLS, getAppExternalOverrides } from '../utils/dev-helper';
import { useMountEffect } from './state/use-mount-effect';

type ImportMap = {
  imports: Record<string, string>;
};

declare global {
  interface Window {
    /** React refresh stubs, installed only for external-UI dev overrides. */
    $RefreshReg$?: () => void;
    $RefreshSig$?: () => (type: unknown) => unknown;
    __vite_plugin_react_preamble_installed__?: boolean;
  }
}

type ImportMapLocation = Pick<Location, 'host' | 'hostname' | 'protocol'>;

type SecureStackUrls = {
  designStudioOrigins: string[];
  hydraOrigin: string;
  secureOrigin: string;
  stackHost: string;
};

const SECURE_TAILSCALE_STACK_HOST_REGEX = /^(?<stack>.+)-secure(?<domain>\.tail\d+\.ts\.net)$/;
const DESIGN_STUDIO_STACK_ASSET_PATH_REGEX = /^(?::\d+)?\/design-studio\/(?<path>.*)$/;
/**
 * Ports the preview stacks have served the Design Studio bundle from. Import
 * maps only match literal prefixes, so every port needs its own entry. The
 * runtime rewrite below accepts any port and does not rely on this list.
 */
const DESIGN_STUDIO_STACK_ASSET_PORTS = [3002, 6109];
let isDesignStudioSecureStackAssetUrlRewriteInstalled = false;

function getSecureStackUrls(location: ImportMapLocation): SecureStackUrls | null {
  if (location.protocol !== 'https:') {
    return null;
  }

  const match = SECURE_TAILSCALE_STACK_HOST_REGEX.exec(location.hostname);
  if (match?.groups == null) {
    return null;
  }

  const stackHost = `${match.groups.stack}${match.groups.domain}`;

  return {
    designStudioOrigins: DESIGN_STUDIO_STACK_ASSET_PORTS.map((port) => `http://${stackHost}:${port}`),
    hydraOrigin: `http://${stackHost}:3002`,
    secureOrigin: `${location.protocol}//${location.host}`,
    stackHost,
  };
}

/**
 * Returns the Design Studio asset path of `url` when it points at the insecure
 * stack host on any port, so it can be served from the secure origin instead.
 */
function getDesignStudioStackAssetPath(url: string, stackHost: string): string | null {
  const insecureStackPrefix = `http://${stackHost}`;
  if (!url.startsWith(insecureStackPrefix)) {
    return null;
  }

  const match = DESIGN_STUDIO_STACK_ASSET_PATH_REGEX.exec(url.slice(insecureStackPrefix.length));
  return match?.groups?.path ?? null;
}

export function getDesignStudioSecureStackImportMapEntries(location: ImportMapLocation = window.location) {
  const urls = getSecureStackUrls(location);
  if (urls == null) {
    return {};
  }

  return Object.fromEntries(urls.designStudioOrigins.map((origin) => [`${origin}/design-studio/`, '/design-studio/']));
}

export function rewriteDesignStudioSecureStackAssetUrl(url: string, location: ImportMapLocation = window.location) {
  const urls = getSecureStackUrls(location);
  if (urls == null) {
    return url;
  }

  const observedRuntimeRewrites: Record<string, string> = {
    'http://localhost:6101/design-studio/api': `${urls.secureOrigin}/design-studio/api`,
    [`${urls.hydraOrigin}/v1`]: `${urls.secureOrigin}/v1`,
  };

  for (const [remoteOrigin, secureOrigin] of Object.entries(observedRuntimeRewrites)) {
    if (url === remoteOrigin) {
      return secureOrigin;
    }

    if (url.startsWith(`${remoteOrigin}/`) || url.startsWith(`${remoteOrigin}?`)) {
      return `${secureOrigin}${url.slice(remoteOrigin.length)}`;
    }
  }

  const designStudioAssetPath = getDesignStudioStackAssetPath(url, urls.stackHost);
  if (designStudioAssetPath == null) {
    return url;
  }

  if (designStudioAssetPath === '__EXTERNAL_CSS_FILES__') {
    return 'data:text/css,';
  }

  return `${urls.secureOrigin}/design-studio/${designStudioAssetPath}`;
}

function patchUrlProperty(
  prototype: HTMLImageElement | HTMLLinkElement | HTMLScriptElement,
  property: 'href' | 'src',
  rewriteUrl: (url: string) => string,
) {
  const descriptor = Object.getOwnPropertyDescriptor(prototype, property);
  if (descriptor?.set == null) {
    return;
  }

  Object.defineProperty(prototype, property, {
    ...descriptor,
    set(value: string) {
      descriptor.set?.call(this, rewriteUrl(value));
    },
  });
}

function installDesignStudioSecureStackAssetUrlRewrite(location: ImportMapLocation = window.location) {
  if (isDesignStudioSecureStackAssetUrlRewriteInstalled || getSecureStackUrls(location) == null) {
    return;
  }

  isDesignStudioSecureStackAssetUrlRewriteInstalled = true;

  const rewriteUrl = (url: string) => rewriteDesignStudioSecureStackAssetUrl(url, location);

  patchUrlProperty(HTMLLinkElement.prototype, 'href', rewriteUrl);
  patchUrlProperty(HTMLScriptElement.prototype, 'src', rewriteUrl);
  patchUrlProperty(HTMLImageElement.prototype, 'src', rewriteUrl);

  const originalSetAttribute = Element.prototype.setAttribute;
  Element.prototype.setAttribute = function setAttribute(name: string, value: string) {
    if ((name === 'href' || name === 'src') && typeof value === 'string') {
      originalSetAttribute.call(this, name, rewriteUrl(value));
      return;
    }

    originalSetAttribute.call(this, name, value);
  };

  const originalFetch = window.fetch.bind(window);
  window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    if (typeof input === 'string') {
      return await originalFetch(rewriteUrl(input), init);
    }

    if (input instanceof URL) {
      return await originalFetch(new URL(rewriteUrl(input.href)), init);
    }

    if (input instanceof Request) {
      return await originalFetch(new Request(rewriteUrl(input.url), input), init);
    }

    return await originalFetch(input, init);
  };
}

export function useInjectImportShim() {
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  if (error != null) {
    throw error;
  }

  useMountEffect(() => {
    async function load() {
      const importShim = getImportShim();
      installDesignStudioSecureStackAssetUrlRewrite();
      const importMap: ImportMap = {
        imports: {
          [AppBundle.Journeys]:
            import.meta.env.VITE_JOURNEYS_ENTRYPOINT_JS_URL ?? 'http://localhost:3002/entrypoint.js',
          [AppBundle.DesignStudio]:
            import.meta.env.VITE_DESIGN_STUDIO_ENTRYPOINT_JS_URL ?? 'http://localhost:3002/design-studio/entrypoint.js',
          ...getDesignStudioSecureStackImportMapEntries(),
        },
      };

      if (import.meta.env.DEV === true || import.meta.env.VITE_ENABLE_EXTERNAL_UI_OVERRIDES === 'true') {
        const externalUiOverrides = getAppExternalOverrides();
        for (const app of Object.values(AppBundle)) {
          if (externalUiOverrides[app] && app in importMap.imports) {
            importMap.imports[app] = APP_EXTERNAL_UI_URLS[app];
          }
        }
      }

      importShim.addImportMap(importMap);

      if (import.meta.env.VITE_ENABLE_EXTERNAL_UI_OVERRIDES === 'true') {
        // TODO: Figure out if we can actually import the refresh runtime
        // correctly. We can't in production-like builds, due to
        // https://github.com/facebook/react/blob/ce6842d8f528977119b80d969306c8475099f66e/packages/react-refresh/src/ReactFreshRuntime.js#L38-L42
        // const runtime = await import('react-refresh/runtime');
        // runtime.injectIntoGlobalHook(window);
        window.$RefreshReg$ = () => undefined;
        window.$RefreshSig$ = () => (type: unknown) => type;
        // This is needed in case a developer runs the external Hydra UI, but
        // works on a vite-built React app on localhost. Much like in the
        // scenario described at https://vitejs.dev/guide/backend-integration.html
        window.__vite_plugin_react_preamble_installed__ = true;
      }

      setIsLoaded(true);
    }

    load().catch((loadError: unknown) => {
      setError(loadError instanceof Error ? loadError : new Error('Failed to inject embedded app import map.'));
    });
  });

  return isLoaded;
}
